"use client";
import React from "react";
import NextLink from "next/link";
import cx from "classnames";
import { usePathname, useRouter } from "next/navigation";
import { useI18n } from "providers/I18nProvider";
import { Locale } from "types/provider/I18n";
import { localizePath, stripLocale } from "utils/locale";

const OPTIONS: { value: Locale; label: string; flag: string; full: string }[] = [
  { value: "vi", label: "VI", flag: "🇻🇳", full: "Tiếng Việt" },
  { value: "en", label: "EN", flag: "🇬🇧", full: "English" },
];

/**
 * Đổi ngôn ngữ = đi sang URL của ngôn ngữ đó (VI: /uu-dai · EN: /en/uu-dai),
 * chứ không còn đổi ngầm trong bộ nhớ trình duyệt. Nhờ vậy mỗi ngôn ngữ có một
 * địa chỉ riêng để Google index, và khách chia sẻ link thì người nhận thấy
 * đúng ngôn ngữ đó.
 */
const LanguageSwitcher: React.FC<{ className?: string }> = ({ className }) => {
  const { locale, setLocale } = useI18n();
  const pathname = usePathname() || "/";
  // Không dùng useSearchParams ở đây: nút này nằm trong Header có mặt trên mọi
  // trang, useSearchParams sẽ khiến các trang tĩnh (vd /hop-tac) không prerender
  // được. Query string (vd /search?searchTerm=…) được đọc từ window lúc click
  // để đổi ngôn ngữ không làm mất kết quả tìm kiếm / bộ lọc đang xem.
  const basePath = stripLocale(pathname);
  const router = useRouter();
  const onSwitch = (e: React.MouseEvent<HTMLAnchorElement>, href: string, value: Locale) => {
    // Cmd/Ctrl/Shift/chuột giữa → để trình duyệt mở tab/cửa sổ mới như link thường
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
    e.preventDefault();
    setLocale(value);
    router.push(`${href}${window.location.search}${window.location.hash}`);
  };

  return (
    <div
      className={cx(
        "flex items-center rounded-full bg-white/25 p-0.5 ring-1 ring-white/40",
        className,
      )}
    >
      {OPTIONS.map((opt) => {
        const active = locale === opt.value;
        const href = localizePath(basePath, opt.value);
        return (
          <NextLink
            key={opt.value}
            href={href}
            hrefLang={opt.value}
            aria-current={active ? "true" : undefined}
            aria-label={opt.full}
            title={opt.full}
            onClick={(e) => onSwitch(e, href, opt.value)}
            className={cx(
              "tap-area inline-flex items-center gap-1 px-2.5 py-1 text-xs font-bold rounded-full transition-colors",
              active
                ? "bg-white text-primary shadow-sm"
                : "text-white/85 hover:bg-white/20",
            )}
          >
            <span aria-hidden="true" className="text-[13px] leading-none">{opt.flag}</span>
            {opt.label}
          </NextLink>
        );
      })}
    </div>
  );
};

export default LanguageSwitcher;
